/**
 * Owns the player's simulation state and glues the movement + intoxication
 * systems together. Keeps a copy of the previous position so rendering can
 * interpolate between fixed steps.
 */

import { PLAYER } from '../config/gameplay';
import { vec3, type Vec3 } from '../utils/math';
import type { CollisionWorld, MoveResult } from '../world/CollisionWorld';
import { beerFraction } from './Beer';
import { IntoxicationSystem, type IntoxicationSample } from './IntoxicationSystem';
import {
  applyLook,
  createPlayerState,
  horizontalSpeed,
  momentumFraction,
  resetPlayerState,
  stepPlayer,
  type MovementInput,
  type PlayerState,
} from './PlayerMovement';

export class PlayerController {
  readonly state: PlayerState;
  readonly intoxication: IntoxicationSystem;

  private readonly previousPosition: Vec3;
  private latestSample: IntoxicationSample;

  constructor(
    private readonly world: CollisionWorld,
    spawn: { position: Vec3; yaw: number },
    intoxication = new IntoxicationSystem(),
  ) {
    this.state = createPlayerState(spawn.position, spawn.yaw);
    this.intoxication = intoxication;
    this.previousPosition = vec3(spawn.position.x, spawn.position.y, spawn.position.z);
    this.latestSample = this.intoxication.sample(0);
  }

  reset(spawn: { position: Vec3; yaw: number }): void {
    resetPlayerState(this.state, spawn.position, spawn.yaw);
    this.previousPosition.x = spawn.position.x;
    this.previousPosition.y = spawn.position.y;
    this.previousPosition.z = spawn.position.z;
    this.latestSample = this.intoxication.sample(0);
  }

  get sample(): IntoxicationSample {
    return this.latestSample;
  }

  get speed(): number {
    return horizontalSpeed(this.state);
  }

  /** The vauhti multiplier the HUD's km/h readout is built on. */
  get momentum(): number {
    return this.state.momentum;
  }

  /** The same thing as 0..1, for the HUD bar. */
  get momentum01(): number {
    return momentumFraction(this.state);
  }

  /** The beer boost as 0..1 of its ceiling, for the HUD's second bar. */
  get beerBoost01(): number {
    return beerFraction(this.state.beer);
  }

  /** Position the eye/interaction checks should use (feet + eye height). */
  get eyePosition(): Vec3 {
    return vec3(
      this.state.position.x,
      this.state.position.y + PLAYER.eyeHeight,
      this.state.position.z,
    );
  }

  /** Unit look vector derived from yaw/pitch (three.js convention). */
  get lookDirection(): Vec3 {
    const cosPitch = Math.cos(this.state.pitch);
    return {
      x: -Math.sin(this.state.yaw) * cosPitch,
      y: Math.sin(this.state.pitch),
      z: -Math.cos(this.state.yaw) * cosPitch,
    };
  }

  /** Mouse look. Applied outside the fixed step - it is already 1:1 with input. */
  applyLook(dx: number, dy: number, sensitivity: number): void {
    applyLook(this.state, dx, dy, sensitivity);
  }

  step(input: MovementInput, simulationTime: number, dt: number, canMove: boolean): MoveResult {
    this.previousPosition.x = this.state.position.x;
    this.previousPosition.y = this.state.position.y;
    this.previousPosition.z = this.state.position.z;
    this.latestSample = this.intoxication.sample(simulationTime);
    return stepPlayer(this.state, input, this.latestSample, this.world, dt, canMove);
  }

  /** Renders between fixed steps without making gameplay frame-rate dependent. */
  interpolatedPosition(alpha: number, out: Vec3): Vec3 {
    out.x = this.previousPosition.x + (this.state.position.x - this.previousPosition.x) * alpha;
    out.y = this.previousPosition.y + (this.state.position.y - this.previousPosition.y) * alpha;
    out.z = this.previousPosition.z + (this.state.position.z - this.previousPosition.z) * alpha;
    return out;
  }
}
